import React from "react";

// Order-flow copy in English, Spanish and Haitian Creole, with a locale hook
// that stays in sync with the site-wide Day25 switcher.
//
// The Day25 switcher (day25.jsx) persists the choice in localStorage under
// "cc-locale-v1" and broadcasts it on window as "carecard:locale". This module
// reads the same key and listens for the same event, so a choice made on the
// homepage is already in effect when the sender reaches /send, and a choice
// made mid-flow applies everywhere at once.
//
// Scope: the flow chrome — headings, field labels, hints, buttons, validation
// nudges, message starters, the rules acknowledgments and the review summary.
// The facility-published mailing rules themselves stay in English: they are
// BSO's words from the policy engine, not ours to translate.

export const FLOW_LOCALE_KEY = "cc-locale-v1";
const LOCALE_EVENT = "carecard:locale";

/** Official BSO arrest search — plain outbound link (new tab). Verified
 *  resolving 2026-09-29. Never scraped, proxied, cached, or stored: the app
 *  only links out so senders can look up the recipient's name/arrest number. */
export const BSO_ARREST_SEARCH_URL = "https://apps.sheriff.org/arrestsearch?d=y";

export const FLOW_COPY = {
  en: {
    stages: { facility: "Facility", recipient: "Recipient", photo: "Photo", message: "Message", rules: "Rules", review: "Review", checkout: "Checkout" },
    stepOf: (i, n) => `Step ${i} of ${n}`,
    flatRate: "$3.99 · flat rate",
    flowStepsAria: "Order steps",
    flowRules: "Rules",
    back: "Back",
    continue: "Continue",
    // ── Facility step ──
    facilityTitle: <>Choose the <em>facility</em></>,
    facilitySub: "Pick the BSO detention facility where your loved one is housed. We show the current mailing address for each one so you can catch a wrong choice before continuing.",
    paulReinNote: "Address confirmed with BSO: PO Box 407003. If you see PO Box 407033 anywhere, that's a typo — don't use it.",
    skipToContent: "Skip to content",
    // ── Photo step ──
    photoTitle: <>Add your <em>photo</em></>,
    photoSub: "One photo fills the front of the card. Take a moment to frame it well — what you see in the frame is exactly what prints.",
    // ── Message step ──
    messageTitle: <>Write your <em>message</em></>,
    messageSub: "Up to 500 characters — something they'll want to read twice.",
    // ── Create stage ──
    photoLabel: "Photograph",
    photoReq: "JPG or PNG · at least 1800×1200px",
    photoHint: "One photo fills the front of the card. It must be at least 1800×1200 pixels so it prints sharp on the 6″ × 4″ postcard the jail accepts (BSO takes 3.5×5″ to 4.25×6″).",
    languageLabel: "Language",
    badType: "Please choose a JPG or PNG photo.",
    tooBig: (mb) => `That file is about ${mb} MB \u2014 please keep photos under 10 MB.`,
    photoTooSmall: (w, h) => `That photo is ${w}×${h} pixels — we need at least 1800×1200 so it prints sharp.`,
    photoTooZoomed: (w, h) => `The framed part of that photo is only ${w}×${h} pixels — zoom out a little, or use a larger photo, so it prints sharp.`,
    photoLoadError: "That photo couldn\u2019t be opened. Try a different JPG or PNG.",
    uploadBtn: "Upload a photo",
    uploadSub: "Tap to choose · up to 10 MB",
    uploading: "Uploading…",
    change: "Change",
    remove: "Remove",
    photoReady: "Photo ready · cropped to 6×4 with full bleed",
    adjustPhoto: "Adjust",
    notHim: (name) => <>Not {name}, or anyone else in custody.</>,
    notHimBody: "BSO destroys cards showing the recipient or another loved one — even an old family photo. We can't detect this, so please check before you upload.",
    // Cropper
    cropTitle: "Frame your photo",
    cropHint: "Drag to move, pinch or use the buttons to zoom, rotate if it's sideways. What you see in the frame is exactly what prints.",
    rotate: "Rotate",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    usePhoto: "Use this photo",
    cancel: "Cancel",
    // Message
    messageLabel: "Message",
    messageReq: "up to 500 characters",
    messagePlaceholder: "Write something they'll want to read twice…",
    messageHint: "No threats, gang references, sexually explicit content, or escape plans.",
    messageTooLong: "That message is over 500 characters \u2014 trim it before continuing.",
    checkPhoto: "Make sure the photo doesn\u2019t show anyone in custody.",
    driveUnavailable: "Drive unavailable \u2014 upload from device.",
    driveChoose: "Choose from Google Drive",
    takePhoto: "Take a photo",
    driveNotConnected: "Google Drive isn\u2019t connected yet \u2014 upload from your device for now.",
    driveOpenError: "Could not open Google Drive. Check your connection and try again, or upload from your device.",
    driveDownloadError: "Could not download that photo from Drive.",
    driveOpening: "Opening Drive\u2026",
    startersLabel: "Need a start? Tap one — it will be added to your message.",
    startersAdded: "Added below — edit it however you like.",
    // ── Recipient step ──
    recipientTitle: <>Who is it <em>for?</em></>,
    recipientSub: "The mailroom needs all three: the booked name, the arrest number, and the housing location. Search the roster and we'll fill them in.",
    recipientSubManual: "The mailroom needs all three: the booked name, the arrest number, and the housing location. Look them up on BSO's official arrest search, then type them in below.",
    findRecipientTitle: "Don't know the details?",
    findRecipientBody: "BSO runs the official arrest search. Look up your recipient there — CareCard can't do the lookup for you — then copy the legal name, arrest number, and housing location into the fields below.",
    findRecipientLink: "Official BSO Arrest Search",
    facilityLabel: "Facility",
    facilityHint: "Pick the BSO detention facility that currently houses your recipient.",
    searchLabel: "Find your recipient",
    searchPlaceholder: "Name or booking number",
    searchHint: "Type at least 3 characters",
    searching: "Searching…",
    enterManually: "Enter details manually",
    noMatch: (q) => `No match for “${q}”. Roster names are often the legal name, not a nickname.`,
    orEnterYourself: "enter the details yourself",
    switchedFacility: (name) => <>We switched the facility to <strong>{name}</strong> to match their record.</>,
    housingFilled: "Housing location filled in from the roster — check it against their latest letter.",
    nameLabel: "Loved one's name",
    nameReq: "up to 50 characters",
    nameHint: "Use the legal name on file with BSO",
    namePlaceholder: "e.g. Marcus Whitfield",
    arrestLabel: "Arrest number",
    arrestReq: "4–20 characters",
    arrestHint: "Letters, numbers, hyphens only",
    arrestPlaceholder: "e.g. 6025-001234",
    whereFind: "Where do I find this?",
    bsoArrestSearch: "Look them up on BSO Arrest Search",
    lookUp: "Look up on BSO",
    newTab: "(opens BSO in a new tab)",
    findSteps: "Type their last name, tap their Arrest No., then copy the name, arrest number, and location back here.",
    housingLabel: "Housing location",
    housingReq: "required",
    housingHint: "On their letters, or ask them. The mailroom routes on it.",
    housingPlaceholder: "e.g. 2B-114",
    sendingTo: "Sending to",
    changeFacility: "Change",
    // Saved recipients
    savedLabel: "Saved on this device",
    savedHint: "Pick a saved recipient to fill this in instantly.",
    remember: "Remember this recipient on this device",
    rememberHint: "Saved only in this browser. Never leaves your phone until you place an order.",
    forget: "Forget",
    forgetConfirm: (name) => `Forget ${name} on this device?`,
    noSaved: "No saved recipients yet.",
    savedPrivacy: "Saved recipients live only in this browser on this device. They are not sent to us, and you can delete them anytime.",
    // Return address
    returnLabel: "Your return address",
    returnOpt: "required",
    returnHint: "Every postcard needs a return address, like ordinary mail. It prints on the card, we hand it to the mail provider for this card, and we do not keep it afterwards.",
    senderName: "Full name",
    senderLine1: "Street address",
    senderLine2: "Apt, suite (optional)",
    senderCity: "City",
    senderState: "State",
    senderZip: "ZIP",
    // ── Review step ──
    reviewTitle: <>Review & <em>send</em></>,
    reviewSub: "One last look at the exact card — front and back — before checkout. About a minute from here.",
    rulesTitle: "The mailing rules for this facility",
    rulesSub: (name) => `These are the rules ${name} applies to routine incoming postcards — the same source the ordering flow checks your card against.`,
    rulesLoading: "Loading the current rules…",
    rulesUnavailable: "The current rules could not be loaded. You can read them on the facility mailing rules page before continuing.",
    ackBody: (name) => <>I confirm I am <strong>18 or older</strong> and that this postcard complies with BSO rules: no threats, gang references, sexually explicit material, escape plans, or other prohibited matter — and that <strong>the photo does not show {name} or anyone else in custody</strong>. I understand that rejected mail is returned at the facility's discretion and is non-refundable once printed.</>,
    likenessBody: (name) => <>The photo does <strong>not</strong> show {name}, or anyone else who is in custody. I understand BSO destroys cards that carry those images, even an old family photo.</>,
    previewTitle: "Preview your postcard",
    previewSub: "6″ × 4″, USPS First-Class. Your photo fills the front; your message prints on the left of the back.",
    frontSide: "Front · image side",
    backSide: "Back · message & address",
    flip: "Flip card",
    to: "To",
    housing: "Housing",
    facility: "Facility",
    mailClass: "Mail class",
    firstClass: "USPS First-Class",
    transit: "Typical time in the post",
    transitValue: "5–8 business days once mailed",
    total: "Total",
    continueToPay: "Continue to payment",
    edit: "Edit",
    needPhoto: "Add a photo to continue.",
    needMessage: "Write a message to continue.",
    needRecipient: "Fill in the recipient details to continue.",
    needRules: "Confirm the mailing rules to continue.",
    // ── Checkout stage ──
    payTitle: <>One last <em>step</em></>,
    paySub: "Review the total, then continue to Stripe's secure hosted checkout.",
    payNoHiddenFees: "No hidden fees.",
    payPostcardTo: (name) => `1 postcard to ${name}`,
    payPrintingPostage: "Printing & postage",
    payIncluded: "included",
    paySalesTax: "Sales tax",
    payTaxNote: "not collected in v1",
    payMin50: "50¢ minimum",
    payTotal: "Total",
    payOptional: "optional",
    payApply: "Apply",
    payRemove: "Remove",
    payEmailLabel: "Email for your receipt",
    payEmailHint: "Used for your payment/order receipt and important order updates.",
    payAcctTitle: "Save this order to an account (optional)",
    payAcctGuest: "Guest checkout works — no account needed. Sign in or create a free account to keep this order in your order history.",
    payAcctSignIn: "Sign in",
    payAcctCreate: "Create account",
    payAcctCreateNote: "Opens in a new tab so your card stays here.",
    payAcctEmail: "Account email",
    payAcctPassword: "Password",
    payAcctSubmit: "Sign in",
    payAcctBusy: "Signing in…",
    payAcctCancel: "Cancel",
    payAcctErr: "That email and password didn't match.",
    payAcctUnverified: "Verify your email first — check your inbox for the link.",
    payAcctSaved: "This order will be saved to your account.",
    payAcctSignedIn: (email) => `Signed in as ${email}.`,
    payStripeTitle: "Payment happens on Stripe.",
    payStripeBody: "CareCard does not collect or store your card number, expiry, or CVC. After you continue, Stripe shows the payment methods available for your device and account. Your card is not eligible for printing until Stripe confirms payment.",
    payTerms: <>I agree to the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>, and I confirm this postcard follows the <strong>facility mail rules</strong>.</>,
    payTermsNote: "Required. The server records acceptance before creating secure Checkout.",
    payContinue: (total) => `Continue to secure payment · ${total}`,
    // Payment is switched off in this build: checkout renders as a clearly
    // labeled demo — the total is real ($3.99 flat) but no payment path runs.
    payDemoTitle: "Checkout is a demo right now",
    payDemoBody: "Payments aren't switched on yet, so this checkout can't take your card and no order is created. The price will stay $3.99 flat when payments go live.",
    payOpening: "Opening secure payment…",
    payStripeBadge: "Stripe-hosted checkout · CareCard does not handle card details",
    // ── Result screens: success, tracking, errors ──
    successPaidLabel: "Payment confirmed",
    successTitleHeld: <>Payment confirmed. <em>Review is next.</em></>,
    successTitle: <>Payment <em>confirmed.</em></>,
    successSubHeld: (total) => <>Stripe confirmed {total}. A person is reviewing the card before anything can be sent to a printer.</>,
    successSub: (total) => <>Stripe confirmed {total}. The card is eligible for the next fulfillment checks; this is not a printing or mailing confirmation.</>,
    successOrderLabel: "Order",
    successCopyId: "Copy order ID",
    successCopied: "Order ID copied",
    successTlPaid: "Payment confirmed",
    successTlPaidSub: (total) => `${total} · Stripe-verified`,
    successTlConfirmed: "confirmed",
    successTlCurrent: "current",
    successTlHeld: "Held for human review",
    successTlHeldSub: "Nothing prints or mails until review clears.",
    successTlAwait: "Awaiting fulfillment evidence",
    successTlAwaitSub: "Tracking advances only when a real provider event supports it.",
    successNoteLead: "Your order ID is",
    successNoteEmailLead: "Updates go to",
    successNoteBody: "CareCard does not claim facility acceptance or recipient delivery because those events are not visible to us.",
    successTrack: "Track order",
    successSendAnother: "Send another",
    // ── Tracking ──
    statusPaid: "Paid",
    statusInReview: "In review",
    statusRendering: "Rendering",
    statusMailed: "Mailed",
    statusInTransit: "In transit",
    statusDelivered: "Delivered",
    statusCanceled: "Canceled",
    statusUnknown: "Unknown",
    trackingHelp: "Help",
    trackingEyebrow: "Tracking",
    trackingCopyId: "Copy order ID",
    trackingCopied: "Copied",
    trackingTo: (name, facility) => `To ${name} at ${facility}`,
    trackingPhotoAlt: "The photo on the front of your card",
    trackingReviewTitle: "A person is reading this card before it prints.",
    trackingReviewBody: "Something in the message matched our facility mail-rule check. It usually clears the same day, and nothing prints or mails until it does. You can still cancel for a full refund while it waits.",
    trackingCanceledTitle: "This card wasn't sent.",
    trackingCanceledContact: "Contact support",
    trackingCanceledAfter: "and we'll sort out your refund.",
    trackPaid: "Payment received",
    trackPaidSub: (total, publicId) => `${total} · order ${publicId}`,
    trackHeld: "Held for review",
    trackHeldSub: "A person checks it before print",
    trackRendering: "Printed & packaged",
    trackRenderingSub: "Print partner · Florida",
    trackMailed: "Entered USPS mail stream",
    trackMailedSub: "Handed to USPS",
    trackTransit: "Out for delivery",
    trackTransitSub: "USPS · Ft. Lauderdale",
    trackDelivered: (facility) => `Delivered to ${facility}`,
    trackDeliveredSub: "Mailroom receives & processes",
    trackCanceled: "Canceled",
    trackCanceledSub: "No card was printed or mailed",
    trackExpectedSuffix: "· expected, not confirmed",
    trackExpectedPrefix: "expected ",
    trackUpcoming: "upcoming",
    trackTimesTitle: "About the times above:",
    trackTimesPrinted: "your card has been handed to our print and mail partner — that part is confirmed. We do not yet receive scan events back from them, so the steps after that are our best estimate of the normal schedule rather than confirmed events.",
    trackTimesEstimate: "we do not yet receive scan events back from our print and mail partner, so every step after payment is our best estimate of the normal schedule rather than a confirmed event.",
    trackTimesHonest: "We would rather say that than show you a delivery we cannot see.",
    trackHeadsUp: "Heads up:",
    trackHeadsUpBody: "BSO mailrooms scan incoming postcards for compliance before distribution. Most cards reach the recipient within 3–5 business days of delivery.",
    // ── Minimal public tracking card (§5.9) ──
    trackDevNote: "Development order — created in a test build, never printed or mailed.",
    trackCancelOrder: "Cancel this order",
    // ── Image rejected ──
    rejectedStep: "Step 3 of 5 · Compose",
    rejectedTitle: "That image can't go through.",
    rejectedBody: "Someone on our team read this card and can't send it. BSO facilities reject postcards containing nudity, weapons, gang insignia, or other prohibited imagery — and we can't submit cards we expect to be returned.",
    rejectedBadge: "REJECTED",
    rejectedWhy: "Why",
    rejectedDefaultReason: "It didn't meet the facility's mail rules.",
    rejectedReviewed: "Reviewed by a person — we don't use automated image scoring. Nothing was printed or mailed.",
    rejectedGood: "Good choices:",
    rejectedGoodBody: "family portraits, outdoor scenes, handwritten notes photographed clearly, children's artwork, pets, a view from home.",
    rejectedContact: "Contact support",
    rejectedRetry: "Upload a different photo",
    // ── Payment failed ──
    failedStep: "Step 5 of 5 · Payment",
    failedTitleBlocked: "We can't mail this message.",
    failedTitleUnknown: "We don't know whether that went through.",
    failedTitle: "We couldn't place your order.",
    failedBodyBlocked: "Facility mail rules don't allow it. Nothing printed — edit the message and try again.",
    failedBodyUnknown: "The connection dropped before the server answered, so your order may or may not have been created. Check your email for an order ID, or look one up, before sending again — sending again now could produce two cards.",
    failedBody: "Nothing printed. Go back and try again.",
    failedReason: "Reason",
    failedDefaultReason: "The order was refused.",
    failedCode: "Code",
    failedNext: "Next steps:",
    failedNextUnknown: "Look up your order before trying again. Your draft is kept on this device.",
    failedNextBody: "Go back, check the details, and try again. Your draft is kept on this device.",
    failedBack: "Back",
    failedRetry: "Try again",
    // ── Schedule locked ──
    lockedEyebrow: "Scheduled send",
    lockedTitle: <>Your card is <em>locked in.</em></>,
    lockedBody: "Your card is scheduled: we hand it to our print partner on the date you picked, and it starts printing the next business day. You can still cancel it any time before printing starts — there is no edit step, so changing anything means cancelling and placing a new order.",
    lockedWindowClosed: "Cancellation window closed",
    lockedSending: "Sending",
    lockedOrderWord: "order",
    lockedPolicy: "Per our cancellation policy, orders can be cancelled for a full refund up to 48 hours before the scheduled send date. After that, the card enters our fulfillment pipeline and cannot be recalled.",
    lockedTlPaid: "Payment received",
    lockedTlWindow: "Cancellation window",
    lockedTlRender: "Render queued",
    lockedTlRenderSub: "Will enter print queue at release time",
    lockedTlMailed: "Mailed",
    lockedContact: "Contact support",
    lockedViewStatus: "View order status",
    lockedAgo: "3 days ago",
    lockedClosedAgo: "Closed 8 hours ago",
    lockedNow: "now",
    lockedIn2: "2 days",
    // ── Moderation unavailable ──
    modTitle: "We can't verify your image right now.",
    modBody: "Both of our automated moderation providers are temporarily unavailable. We never submit postcards that haven't been screened, so please try again in a few minutes.",
    modNotify: "Get notified when ready",
    // ── Flow stage extras ──
    flowLivePreview: "Live preview",
    flowLivePreviewHint: "updates as you type",
    flowPreviewLabel: "Live postcard preview",
    flowPreviewEmpty: "Your photo fills the front",
    flowPreviewEmptyMessage: "Your message will appear here.",
    flowFlipBack: "back",
    flowFlipFront: "front",
    flowFlipLabel: (label, side) => `${label} — showing ${side}. Activate to flip.`,
    flowBackDesign: "Back design",
    flowOptional: "optional",
    flowBackDesignHint: "Pick how the message side is framed. The photo side is always full-bleed.",
    flowOrderSummary: "Order summary",
    flowCards: (n) => `${n} cards`,
    flowCardSingular: "card",
    flowIncluded: "Included",
    flowTotal: "Total",
    flowPrintingPostage: "Printing + postage",
    flowCardLabel: (i, who) => (who ? `Card ${i + 1} — ${who}` : `Card ${i + 1}`),
    // ── Order lookup ──
    lookupTitle: <>Look up your <em>postcard.</em></>,
    lookupOrderId: "Order ID",
    lookupHint: "16 characters · letters and numbers",
    lookupHelp: "Need help",
    lookupSubmit: "Look up order",
    // ── Checkout page states ──
    confirmingTitle: "Confirming payment",
    confirmingHead: "Confirming with Stripe…",
    confirmingBody: "We are checking the payment with Stripe before showing an order confirmation.",
    processingTitle: "Payment still processing",
    processingHead: "Stripe has not confirmed payment yet",
    processingBody: "Nothing will be printed while payment is unconfirmed. Use your order ID to check again shortly.",
    leftPaymentHead: "You left secure payment",
    leftPaymentTrack: "Track order",
    leftPaymentRetry: "Start again",
    noOrderHead: "There is no order on this page",
    noOrderTrack: "Track an order",
    noOrderBack: "Back to sending",
    noOrderTitle: "No order to show",
    noOrderBody: "This page confirms an order at the moment it is placed, and it does not keep one. If you placed an order, its ID was emailed to you and you can look it up. If you were part-way through sending a card, your draft is still there.",
    sentConfirmedTitle: "Order confirmed",
    cancelTitle: "Payment not completed",
    cancelBody: "CareCard has not treated this order as paid, and nothing can print while payment is unconfirmed. Stripe may keep the Checkout Session open briefly; it expires automatically if it is not completed.",
    lookupPageTitle: "Track your order",
    lookupPageDesc: "Enter your 16-character order ID to see where your postcard is.",
    pausedTitle: "Sending is paused",
    pausedDesc: "New CareCard Express orders are temporarily paused. Order tracking and cancellation still work.",
    pausedHead: "Sending is paused right now",
    pausedBody: "We've temporarily stopped taking new cards. Nothing has been charged. Orders already placed are unaffected \u2014 you can still track or cancel them, and we'll email you at each step as usual.",
    pausedContact: "Contact us",
    pausedWaitlist: "Join the waitlist",
    notfoundHome: "Home",
  }
};

// Tappable message starters. Tapping APPENDS — it never replaces text the
// sender already wrote.
/** Append a starter to a message draft; never replaces existing text. */
export function appendStarter(message, starter) {
  const cur = (message || "").trimEnd();
  return cur ? cur + "\n\n" + starter : starter;
}
export const STARTERS = {
  en: [
    { group: "Thinking of you", items: [
      "Thinking of you every day.",
      "You're not forgotten. Write back soon.",
      "Sending you a little piece of home with this card.",
    ] },
    { group: "Encouragement", items: [
      "Stay strong — this isn't forever.",
      "Keep your head up. We're out here fighting for you.",
      "We love you and we're proud of you.",
    ] },
    { group: "Family news", items: [
      "Counting down the days until you're home.",
      "Everyone here is doing okay — we talk about you all the time.",
      "The kids send their love and ask about you every day.",
    ] },
    { group: "Birthday & holidays", items: [
      "Happy birthday! Celebrating you from here until we can do it together.",
      "Thinking of you even more today — happy holidays from everyone at home.",
      "Missing you at the table today. Saving you a seat, always.",
    ] },
  ]
};

// ── Lazy locale loading ─────────────────────────────────────────────────────
// The initial download ships English only. Spanish and Haitian Creole copy
// lives in split chunks (copy/flow_es.jsx, copy/flow_ht.jsx) that load the
// first time a visitor actually chooses that language, then register into
// the same FLOW_COPY / SITE_COPY / STARTERS objects the rest of the app
// already reads. Until a chunk lands, the hooks fall back to English, so
// nothing ever renders blank.
const KNOWN_LOCALES = ["en", "es", "ht"];
const _lazyLocales = {
  es: () => import("./copy/flow_es.jsx"),
  ht: () => import("./copy/flow_ht.jsx"),
};
const _localePending = {};

export function loadLocale(locale) {
  if (locale === "en" || FLOW_COPY[locale] || !_lazyLocales[locale]) {
    return Promise.resolve(null);
  }
  if (!_localePending[locale]) {
    _localePending[locale] = _lazyLocales[locale]().then((mod) => {
      FLOW_COPY[locale] = mod[`FLOW_${locale.toUpperCase()}`];
      SITE_COPY[locale] = mod[`SITE_${locale.toUpperCase()}`];
      STARTERS[locale] = mod[`STARTERS_${locale.toUpperCase()}`];
      return mod;
    });
  }
  return _localePending[locale];
}

function readLocale() {
  try {
    const saved = localStorage.getItem(FLOW_LOCALE_KEY);
    if (saved && KNOWN_LOCALES.includes(saved)) return saved;
  } catch {}
  const lang = (typeof navigator !== "undefined" ? navigator.language : "en").toLowerCase();
  if (lang.startsWith("es")) return "es";
  if (lang.startsWith("ht")) return "ht";
  return "en";
}

// A returning visitor with a saved non-English language starts the chunk
// download at bundle load, not after first paint.
try {
  const _warm = readLocale();
  if (_warm !== "en") loadLocale(_warm);
} catch {}

/** Order-flow locale. Same storage key and broadcast event as the Day25
 *  switcher, so the whole site shares one language choice. */
export function useFlowLocale() {
  const [locale, setState] = React.useState(readLocale);
  const [, setLocaleLoaded] = React.useState(0);
  React.useEffect(() => {
    const handler = (event) => {
      const next = event?.detail?.locale;
      if (next && KNOWN_LOCALES.includes(next)) setState(next);
    };
    window.addEventListener(LOCALE_EVENT, handler);
    return () => window.removeEventListener(LOCALE_EVENT, handler);
  }, []);
  // Pull the language chunk the first time it is chosen; while it is in
  // flight the copy below falls back to English rather than blank.
  React.useEffect(() => {
    let live = true;
    loadLocale(locale).then(() => {
      if (live) {
        document.documentElement.lang = locale;
        setLocaleLoaded((n) => n + 1);
      }
    });
    return () => { live = false; };
  }, [locale]);
  const setLocale = React.useCallback((next) => {
    if (!KNOWN_LOCALES.includes(next)) return;
    try { localStorage.setItem(FLOW_LOCALE_KEY, next); } catch {}
    if (typeof document !== "undefined") document.documentElement.lang = next;
    setState(next);
    window.dispatchEvent(new CustomEvent(LOCALE_EVENT, { detail: { locale: next } }));
  }, []);
  const t = FLOW_COPY[locale] || FLOW_COPY.en;
  const starters = STARTERS[locale] || STARTERS.en;
  return { locale, t, starters, setLocale };
}

/** Compact EN/ES/HT toggle for the order flow header. */
export function FlowLanguageSwitcher() {
  const { locale, t, setLocale } = useFlowLocale();
  return (
    <div className="flow-languages" role="group" aria-label={t.languageLabel}>
      {[["en", "EN"], ["es", "ES"], ["ht", "HT"]].map(([id, label]) => (
        <button
          key={id}
          type="button"
          aria-pressed={locale === id}
          aria-label={id === "en" ? "English" : id === "es" ? "Español" : "Kreyòl"}
          className={"flow-lang" + (locale === id ? " active" : "")}
          onClick={() => setLocale(id)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── The three customer-facing stages (plus checkout) ──────────────────────
// Lives in flow_steps.js (dependency-free) so the router can know the step
// paths without pulling this module's EN/ES/HT copy into the initial
// download. Re-exported here so existing importers keep working.
import { FLOW_STEPS, LEGACY_STEP_TO_STAGE } from "./flow_steps";
export { FLOW_STEPS, LEGACY_STEP_TO_STAGE };

/** The seven-dot stepper the live flow renders on every step (WEB-02 §15.2). */
export function FlowNav({ stepIndex }) {
  const { t } = useFlowLocale();
  const labels = [t.stages.facility, t.stages.recipient, t.stages.photo, t.stages.message, t.stages.rules, t.stages.review, t.stages.checkout];
  const here = t.stepOf(stepIndex + 1, FLOW_STEPS.length);
  return (
    <>
      <div className="app-nav">
        <div className="app-logo">
          <div className="sym">C</div>
          <div className="wordmark">Care<em>Card</em></div>
        </div>
        <FlowLanguageSwitcher />
      </div>
      <div className="step-label">
        <span>{here} · {labels[stepIndex]}</span>
        <span className="count">{t.flatRate}</span>
      </div>
      <div className="stepper" role="group" aria-label={here}>
        {labels.map((l, i) => (
          <div key={l} className={"dot " + (i < stepIndex ? "done" : i === stepIndex ? "current" : "")} />
        ))}
      </div>
      {/* Governed progress rail: Facility → Recipient → Photo → Message →
          Rules → Review → Checkout. The labels name the real steps and keep
          the $3.99 badge on screen. Visual only — no flow change. */}
      <div className="cc-flow-rail" role="group" aria-label={t.flowStepsAria}>
        {[t.stages.facility, t.stages.recipient, t.stages.photo, t.stages.message, t.stages.rules, t.stages.review].map((l, i) => (
          <span key={l} className="cc-flow-rail-item"
            data-active={i === stepIndex ? "true" : "false"}>
            {l}
          </span>
        ))}
        <span className="cc-price-badge">$3.99</span>
      </div>
    </>
  );
}

// ── Site-wide chrome copy (EN/ES) ─────────────────────────────────────────
// Extends the flow locale infrastructure: the same storage key
// ("cc-locale-v1"), the same "carecard:locale" broadcast, and the same
// <html lang> update, so a language choice made in the order flow applies to
// the whole site and vice versa. The chrome offers EN/ES/HT, matching the order flow.
//
// Facility-published mailing rules stay in English inside the Mail Guide:
// they are BSO's words from the policy engine, not ours to translate (same
// scope rule as the flow's Rules step).

export const SITE_LOCALES = ["en", "es", "ht"];

export const SITE_COPY = {
  en: {
    // ── Global chrome ──
    skipToContent: "Skip to main content",
    navSend: "Send a card",
    navMailGuide: "Mail guide",
    navServices: "Services",
    navFaq: "FAQ",
    navTrack: "Track order",
    navAbout: "About",
    navContact: "Contact",
    navSecurity: "Security",
    languageLabel: "Language",
    themeLabel: "Theme",
    themeSwitchName: "Light theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    accentLabel: "Accent color",
    accentGold: "Gold",
    accentRose: "Rose",
    accentEmerald: "Emerald",
    a11yLabel: "Accessibility",
    textSizeLabel: "Text size",
    textSizeStandard: "Standard",
    textSizeLarge: "Large",
    textSizeXLarge: "Extra large",
    contrastLabel: "High contrast",
    contrastOn: "On",
    contrastOff: "Off",
    footerDisclaimer: (
      <>
        <strong>CareCard Express is not affiliated with, endorsed by, or sponsored by the Broward Sheriff's Office.</strong>{" "}
        We are an independent service that prints and mails postcards via USPS in compliance with BSO mail policy.
      </>
    ),
    footerTagline: "A message that matters.",
    footerRights: "© 2026 CareCard Express LLC · serving Broward County, FL",
    // ── Share with a friend (owner add-on, 2026-10-13) ──
    shareCta: "Share with a friend",
    shareTitle: "CareCard Express — A message that matters.",
    shareText: "CareCard Express turns a photo and a short message into a real postcard, printed and mailed to your loved one at a Broward County facility — $3.99 flat. Now open:",
    shareMenuLabel: "Share options",
    shareCopy: "Copy link",
    shareCopied: "Link copied",
    shareSms: "Text message",
    shareWhatsapp: "WhatsApp",
    shareEmail: "Email",
    // ── Optional profile ──
    profileButton: "Save my info (optional)",
    profileTitle: "Save my info — optional",
    profileName: "Name",
    profileEmail: "Email",
    profileNote: "Optional. Stored only in this browser on this device — never sent anywhere, no account, no password. Guest checkout works fine without it.",
    profileSave: "Save on this device",
    profileSavedAs: (name) => `Saved as ${name}`,
    profileRemove: "Remove",
    profileCancel: "Cancel",
    profileGreeting: (name) => `Hi, ${name}`,
    // ── Trust badges ──
    badgesTitle: "Why send with CareCard",
    badgeSecure: "Secure checkout",
    badgeSecureSub: "Stripe-hosted payment. CareCard never sees or stores your card number.",
    badgeNoAccount: "No account needed",
    badgeNoAccountSub: "Guest checkout — a card takes about two minutes, nothing to sign up for.",
    badgeMailRules: "Checked against facility mail rules",
    badgeMailRulesSub: "Your card is reviewed against the facility's published mail rules before it prints.",
    badgeUsps: "USPS First-Class postage included",
    badgeUspsSub: "Printed 6″ × 4″ and mailed First-Class. One flat price, no surprises.",
    testMode: "Test mode",
    testModeBanner: "Test mode — no real charge. Payments are simulated until launch.",
    // ── Mail guide ──
    mailGuideKicker: "Mail guide",
    mailGuideTitle: "Facility mail rules",
    mailGuideSub: "What each supported Broward County facility accepts — addressing, photos, messages, and prohibited content. The rules below are BSO's published mail policy; per-facility differences beyond the mailing address are not established in BSO's published sources.",
    mailGuidePendingBanner: "Pending facility confirmation. These rules reflect BSO's published mail policy (BSO Inmate Handbook, Revised 2022, and the BSO mail policy page). They are not an official approval from any facility, and a facility can change its rules without notice — always check the facility's own page if you are unsure.",
    mailGuideAddressing: "How to address it",
    mailGuideAddressLabel: "Mailing address",
    mailGuidePhotoRules: "Photo rules",
    mailGuideMessageRules: "Message rules",
    mailGuideProhibited: "Prohibited",
    mailGuideRulesSource: "Rules source",
    mailGuideSourcePublished: "BSO published sources (BSO Inmate Handbook, Revised 2022 · BSO mail policy page), via the CareCard policy engine.",
    mailGuideSourceApi: "Facility mail-guide API — live facility data.",
    mailGuidePendingTag: "Pending confirmation",
    mailGuideRuleNote: "Rules are shown in English as BSO published them.",
    // ── Services ──
    servicesKicker: "Services",
    servicesTitle: "What you can send",
    servicesSub: "One flat price per card. No accounts, no subscriptions, no hidden fees.",
    serviceSingleTitle: "Single postcard",
    serviceSingleBody: "Your photo printed full-bleed on a 6″ × 4″ postcard, your message on the back, mailed USPS First-Class to a supported Broward County facility. About two minutes, guest checkout.",
    serviceSinglePrice: "$3.99",
    serviceSinglePriceNote: "flat · postage included",
    serviceMultiTitle: "Multi-card orders",
    serviceMultiBody: "Send several cards at once — to one person or several. Each card is priced individually; there are no bulk tiers and no minimum.",
    serviceMultiPrice: "$3.99",
    serviceMultiPriceNote: "per card",
    serviceScheduledTitle: "Scheduled delivery",
    serviceScheduledBody: "Pick a future date and we hand your card to our print partner that day. It starts printing the next business day and goes into the mail right after printing — we can't promise an arrival date. Scheduling is free — the card price doesn't change.",
    serviceScheduledPrice: "$3.99",
    serviceScheduledPriceNote: "per card · scheduling free",
    servicePhotoTitle: "Photo printing",
    servicePhotoBody: "Every card includes full-bleed photo printing from your JPG or PNG (minimum 1800×1200px so it prints sharp). There is no separate photo-credit product.",
    serviceSavedTitle: "Saved recipients",
    serviceSavedBody: "Optionally remember a recipient on this device to refill the form in one tap. Saved only in this browser — never sent to us, deletable anytime.",
    serviceSavedPrice: "Free",
    serviceReferralTitle: "Referrals",
    serviceReferralBody: "Share your referral link from any order page. A referral credit program is planned but not available yet — referrals currently earn no credit.",
    serviceReferralSoon: "Planned",
    serviceCreditsTitle: "Photo credits",
    serviceCreditsBody: "Prepaid photo/card credits are not offered yet. Every order is pay-per-card at the flat rate.",
    serviceCreditsSoon: "Not available yet",
    servicesCta: "Start an order",
    // ── Homepage ──
    homeHeaderHow: "How it works",
    homeHeaderPricing: "Pricing",
    homeHeaderTrack: "Track",
    homeHeaderHelp: "Help",
    homeMenuOpen: "Open menu",
    homeMenuClose: "Close menu",
    homeNavTrackOrder: "Track an order",
    homeNavFacilityRules: "Facility rules",
    homeNavAbout: "About",
    homeNavContact: "Contact",
    homeHeaderSignIn: "Sign in",
    homeHeaderCreateAccount: "Create account",
    homeHeaderMyAccount: "My account",
    homeHeaderSignInShort: "Sign in",
    homeHeaderMyAccountShort: "Account",
    homeFooterSend: "Send",
    homeFooterTrack: "Track",
    homeFooterHelp: "Help",
    homeFooterFacilityRules: "Facility rules",
    homeFooterFacilities: "Facilities",
    homeFooterContentPolicy: "What can go on a card",
    homeFooterRefunds: "Refunds",
    homeFooterCancelling: "Cancelling",
    homeFooterDataRetention: "Data retention",
    homeFooterPrivacy: "Privacy",
    homeFooterTerms: "Terms",
    homeFooterAcceptableUse: "Acceptable use",
    homeFooterAccessibility: "Accessibility",
    homeFooterSecurity: "Security",
    homeFooterDisclosures: "Disclosures",
    homeFooterStatus: "Status",
    homeFooterLegal: "CareCard Express is an independent service. We are not affiliated with the Broward Sheriff's Office, any detention facility, USPS, or any payment or print provider.",
    homeHeroEyebrow: "Broward County · BSO facilities",
    homeHeroTitle1: "A Message",
    homeHeroTitle2: "That Matters",
    homeHeroLede: "Turn a photo and message into a real physical postcard for your loved one. We print it, address it the way the facility requires, and put it in the post.",
    homeHeroCta: "Create Postcard",
    homeGiveawayText: "Giveaway — the first 10 cards are free.",
    homeGiveawayLink: "Claim a free card",
    homeHeroSecondary: "Send a Message That Matters",
    homeHeroSteps: "Photo • Message • Review • $3.99 • We print & mail",
    homeHeroPostcard: "REAL 4x6 POSTCARD",
    homeNoHiddenFees: "No hidden fees.",
    homeHeroTrack: "Track an order",
    homeTrustNoAccount: "No account required",
    homeTrustSecure: "Secure checkout",
    homeTrustNotYet: "Card payments not yet switched on",
    homeTrustRules: "Built around facility mailing rules",
    homeTrustLabel: "What to expect",
    homeProofEyebrow: "The object",
    homeProofTitle: "A real postcard, not another message bubble",
    homeProofLede: "What arrives is a printed card with your photo across the front and your words on the back. It goes through the mailroom like any other postcard, because that is what it is.",
    homeProofFront: "Front",
    homeHeroSampleMessage: "Thinking about you every single day.\n\nThe garden finally came in. Dad says hello.\n\nWe are counting the weeks.\n\nLove,\nMom",
    homeProofSampleMessage: "Grandma read your letter out loud twice.\n\nWe put your drawing on the fridge.\n\nSee you on visitation Sunday.",
    homeProofFrontBody: "Your photo + message • Printed and mailed. Your photo fills the front, edge to edge. Nothing of ours is printed on it.",
    homeProofBack: "Back",
    homeProofBackBody: "Your message, in black ink, in the area the facility allows.",
    homeProofSide: "Right-hand side",
    homeProofSideBody: "Reserved for the address, postage and postal barcode, which the mail partner prints.",
    homeProofFlipHint: "Turn the card over with the Front and Back controls.",
    homeProofFlipHintReduced: "Use the Front and Back controls to see both sides.",
    cardToggleAria: "Postcard side",
    cardNote: "A preview of what will be printed. The mail partner adds the address and postage in the reserved areas.",
    cardPhotoAria: "A sample photograph standing in for the photograph you would send",
    cardPhotoAlt: "The photo on the front of your postcard",
    cardPhotoEmpty: "Your photo",
    cardPhotoEmptySub: "Fills the whole front of the card",
    cardMessageEmpty: "Your message goes here.",
    cardZoneAddress: "Address & postage",
    cardZoneAddressSub: "Printed by the mail partner",
    cardZoneBarcode: "Postal barcode",
    homeWhyEyebrow: "Why families choose us",
    homeWhyTitle: "Why CareCard Express",
    homeReasons: [
      { t: "Made for physical mail", d: "The result is a real postcard, not a message someone has to pay to read on a tablet." },
      { t: "Guided by mailing rules", d: "The requirements that get mail rejected are shown before you order, not after." },
      { t: "Simple from start to finish", d: "No account, no subscription, no design tool to learn." },
    ],
    homeComplianceEyebrow: "Getting it accepted",
    homeComplianceTitle: "We help you get the details right",
    homeComplianceLede: "Mail to a detention facility is refused for small, specific reasons: a missing arrest number, the wrong housing location, a photo that cannot be printed on the card. Each of those is checked with you before you pay.",
    homeComplianceChecks: [
      "The recipient's name, arrest number and housing location",
      "The facility's own mail address, not its street address",
      "The current form and content rules, shown in full before you agree to them",
      "A preview of both sides of the card",
    ],
    homeFacilitiesTitle: "Facilities we send to",
    homeFacilitiesLoading: "Loading facilities…",
    homeFacilityAvailable: "Available",
    homeFacilityUnavailable: "Unavailable",
    homeFacilityNote: "When official sources disagree about a facility's mailing address, we stop sending there until it is confirmed. It is better than posting a card to the wrong box.",
    homeTeaserEyebrow: "Before you pay",
    homeTeaserTitle: "See the card you are sending",
    homeTeaserCta: "Create yours",
    homeTeaserCaption: "An illustration of the creation screen, not the live editor.",
    homeTeaserCharCount: "184 / 500 characters",
    homeTeaserPhoto: "Photo",
    homeTeaserMessage: "Message",
    homeTeaserFacility: "Facility",
    homeTeaserRecipient: "Recipient",
    homeJourneyEyebrow: "After you order",
    homeJourneyTitle: "What happens next",
    homeJourneySteps: [
      { t: "Order received", d: "You get an order number straight away." },
      { t: "Reviewed", d: "A person checks the photo and the message against the facility's rules." },
      { t: "Sent to the print partner", d: "Once it is approved, the card goes to print." },
      { t: "Mailed", d: "We tell you when the print partner hands it to USPS." },
    ],
    homeJourneyNote: "We can tell you when a card was posted. We cannot tell you that a facility accepted it or that it reached the person — nothing we receive proves either, so we do not say it.",
    homePricingEyebrow: "Flat pricing",
    homePricingTitle: "One price",
    homePricingUnit: "per postcard",
    homePricingIncludes: [
      "One physical postcard, printed and mailed",
      "No account required",
      "A full preview before you pay",
      "No subscription, and nothing recurring",
    ],
    stampArtLines: ["A message", "that", "matters."],
    stampMonoLines: ["Photo postcard", "Print + postage incl."],
    stampPostmarkLines: ["First class", "With love"],
    stampAria: "$3.99 per postcard — printing and postage included.",
    homeTrustEyebrow: "Trust & privacy",
    homeTrustTitle: "How we handle this",
    homeTrustClose: "Let them know they're not forgotten.",
    homeTrustItems: [
      { t: "Your photo is not kept forever", d: "Photographs are destroyed on a published schedule, and sooner if you ask." },
      { t: "Your details stay off the tracking page", d: "Anyone with the order number sees a status. They do not see the recipient, the message or your address." },
      { t: "Every card is seen by a person", d: "Photos are reviewed before printing, so a card is not refused at the mailroom for something we could have caught." },
      { t: "We say what we know", d: "Order states come from things that happened, not from a timer." },
    ],
    homeFaqEyebrow: "Questions",
    homeFaqTitle: "Before you start",
    homeFaqItems: [
      { q: "What exactly gets mailed?", a: "A 4x6 inch printed postcard: your photograph across the front, your message on the back. The mail partner adds the address, postage and barcode." },
      { q: "What photo can I use?", a: "A JPEG or PNG, at least 1800 x 1200 pixels, under 10 MB. It cannot show the person you are writing to, another person in custody, weapons, or anything sexual — those are the facility's rules, not ours." },
      { q: "What do I need to know about the recipient?", a: "Their full name as booked, their arrest or CIS number, and their current housing location. All three go on the card." },
      { q: "What if a facility is unavailable?", a: "We show it greyed out with the reason. Usually it means official sources disagree about the mailing address, and we will not guess." },
      { q: "Can I see it before I pay?", a: "Yes. You see both sides of the card, with the reserved postal areas marked, before checkout." },
      { q: "Do I need an account?", a: "No. You get an order number instead, and that is all you need to track or cancel." },
      { q: "Can I cancel?", a: "While the card has not gone to print, yes. Once it is printing it cannot be recalled, and we tell you which one it is." },
      { q: "Why are the mailing rules so strict?", a: "Broward moved routine incoming mail to postcards only in 2021. A card that breaks the form or content rules is destroyed at the mailroom rather than returned." },
    ],
    homeFinalTitle: "A MESSAGE THAT MATTERS.",
    homeFinalCta: "Send a Postcard",
    homeFinalNote: "No account. One card.",
    homeHowMore: "See every step",
    homeMoreLabel: "Learn more",
    homeMoreFacilities: "Facilities & addresses",
    homeMoreFaq: "Common questions",
    homeHowEyebrow: "How it works",
    homeHow: [
      { t: "Create", d: "Choose a photo and write your message.", check: "Photo and message checked against the facility's mailing rules." },
      { t: "Check", d: "We guide you through recipient details and BSO mailing rules.", check: "Recipient details and BSO mailing rules checked before checkout." },
      { t: "Send", d: "Pay $3.99 — we can tell you when your card was posted.", check: "Priced before you pay. We can tell you when it was posted." },
    ],
    homeComplianceAddressLabel: "Mailing address",
    // ── Why a postcard matters (harvest integration, 2026-10-21) ──
    // Person-first framing lifted from the fleet strategy harvest; the copy
    // is fresh production copy. Research is context, never an outcome
    // promise. The refund tile states the real policy: full refund before
    // print / in review, non-refundable once printed.
    whyEyebrow: "Why a postcard",
    whyTitle: "Why a postcard matters",
    whyLede: "A phone call ends. A message on a tablet is read once, between everything else on the screen. A postcard is different: it is held in two hands, read again at lights-out, and kept where it can be seen every day.",
    whyBody: "For the person inside, mail is proof the world outside is still there — still cooking, still growing, still counting the weeks with them. For the person sending, it is the one thing you can still put in their hands.",
    // Studio fold-in (2026-10-21): emotional lines from the ChatGPT Sites
    // "CareCard Express Studio" page, owner-approved as candidate copy.
    // Spec-checked before landing: no outcome promise, no banned word,
    // the bare-card truth is stated outright in whyMoments.
    whyPullLine: "Close to them. Even from here.",
    whyMoments: "It's the kids getting taller. The dog being the dog. An ordinary Tuesday they wish they were part of. A postcard puts that moment in their hands — your photo on the front, your message on the back.",
    whyClosing: "Distance doesn't have to mean disconnection. Send a little piece of home.",
    whyFounder: "Built with care, by someone who gets it. Founded by Bianca “Bee” Rodriguez, for the people who keep showing up for the people they love.",
    whyResearch: "Researchers who study families separated by incarceration describe regular contact — letters and cards included — as one of the things that helps people cope inside and reconnect after release. We are not researchers, and a postcard is not a program. It is a small, real thing that reaches them.",
    whyPhotoAlt: "A mother and daughter looking at a photo postcard together",
    whyTiles: [
      { t: "Checked against the rules", d: "Every photo and message is reviewed against the facility's published mail rules before it prints." },
      { t: "$3.99 flat", d: "One price per card — printing and First-Class postage included. No account, no subscription." },
      { t: "You know when it posts", d: "Order states come from real events: approved, printed, handed to USPS. We never guess the rest." },
      { t: "Honest refunds", d: "A full refund any time before your card prints, including while it is in review. Once printed it cannot be recalled, so printed cards are not refundable." },
    ],
    // ── Seasonal: Christmas send-by page (/christmas) ──
    // Copy standard verified against the fleet Christmas assets (QC
    // 2026-10-20): send-by headline, slow-December-mailrooms line,
    // "$3.99 flat.", NOW OPEN badge in English on every locale (the badge
    // is rendered as a literal in seasonal.jsx, never from these tables).
    // The send-by date itself is date-driven from seasonal.jsx SEASON.
    seasonalEyebrow: "Christmas · Broward County",
    seasonalTitleOpen: (dateStr) => `Send by ${dateStr} to get there before Christmas.`,
    seasonalTitleLate: "Christmas mail call is still open.",
    seasonalLedeOpen: "Mailrooms run slow in December — order before the rush and make mail call feel like Christmas morning.",
    seasonalLedeLate: (dateStr) => `The ${dateStr} send-by date has passed, so a card sent now may arrive after Christmas. It still arrives — and it is still read twice.`,
    seasonalCta: "Send a Message That Matters",
    seasonalCtaSecondary: "Create Postcard",
    seasonalPriceNote: "$3.99 flat — print and postage included. No account, no subscription.",
    seasonalCardMessage: "Merry Christmas from all of us at home.\n\nKat is asleep on your side of the couch, saving it for you.\n\nWe love you. We are counting the weeks.\n\nLove, Mom",
    seasonalHowEyebrow: "How the deadline works",
    seasonalHowTitle: "Why December 11",
    seasonalHowBody: [
      "Order by the date above and your card enters the mail with its best chance of being there before Christmas.",
      "We can tell you when your card was posted. We cannot promise when a mailroom hands it over — nothing we receive proves that, so we do not say it.",
      "Missed the date? Send anyway. A late card is still opened, still read, and still kept.",
    ],
    seasonalFacilitiesNote: "We send to all four Broward County facilities, addressed the way each mailroom requires — Main Jail: PO Box 9356, Fort Lauderdale, FL 33310.",
    seasonalPhotoCaption: "Your photo fills the front of the card. Nothing of ours is printed on it.",
    seasonalAltFamily: "A family holding a photo postcard",
    seasonalFinalTitle: "MAKE MAIL CALL FEEL LIKE CHRISTMAS MORNING.",
    seasonalBannerOpen: (dateStr) => `Christmas mail call — send by ${dateStr} to get there before Christmas.`,
    seasonalBannerLate: "Christmas mail call — the send-by date has passed, but a late card still lands.",
    seasonalBannerLink: "Christmas send-by page",
    seasonalBannerDaysLeft: (n) => (n <= 0 ? "Today is the last day." : n === 1 ? "1 day left." : `${n} days left.`),
    // ── Seasonal: local section + plain-language FAQ (Studio fold-in,
    // 2026-10-21) ──
    // Section rhythm follows the Studio page: 01 show up / 02 local by
    // choice / 03 peace of mind. The numbers render as literals in
    // seasonal.jsx (locale-neutral, like the NOW OPEN badge). The FAQ
    // answers are Studio's plain language, fact-checked against the
    // governed FAQ (faq_data.js) before landing: guest checkout is real,
    // the preview precedes payment, a person reviews every card before
    // print, order updates report provable events only, and no facility
    // may be substituted for another. Where Studio's wording was vaguer
    // than the governed answer (track/cancel), the governed answer
    // stands and the Studio entry was not imported.
    seasonalLocalEyebrow: "Local by choice",
    seasonalLocalTitle: "Made for Broward. Made for your people.",
    seasonalFaqEyebrow: "A little peace of mind",
    seasonalFaqTitle: "Good questions. Clear answers.",
    seasonalFaqHelp: "Need a hand? Talk to us",
    seasonalFaq: [
      { q: "What exactly gets mailed?", a: "One real 4×6 photo postcard — your photo on the front, your message on the back. It is mailed bare: the address and postage print directly on the card." },
      { q: "Do I need an account?", a: "No. You can create and pay for a postcard as a guest — that is how most people send. An account is optional, for easier return visits." },
      { q: "What should I have ready?", a: "Your loved one's name exactly as booked, their arrest (CIS) number, their current housing location, and their facility. Check that the details are current before you order — the ordering flow walks you through each one." },
      { q: "Can I preview my postcard before paying?", a: "Yes. The ordering flow shows your postcard for review before payment. After you order, a person also checks the photo and message against the facility's mail rules before anything prints." },
      { q: "How long will it take to arrive?", a: "Postal and facility processing times vary, and mailrooms add their own time. Your order updates show the events we can prove. A postal or provider delivery update means the card reached the facility's mail stream — it does not confirm that your loved one has received it." },
      { q: "What if my facility isn't available?", a: "We serve the four Broward County facilities. If yours is not listed, do not choose a different facility as a substitute — the card would go to the wrong mailroom. Contact us before ordering and we will help." },
    ],
    prefsLabel: "Preferences",
    flowStepsAria: "Order steps",
    flowRules: "Rules",
  }
};

/** Chrome/i18n copy for a locale, falling back to English. */
export function getSiteCopy(locale) {
  return SITE_COPY[locale] || SITE_COPY.en;
}

/** Site-wide locale hook. Same storage key, broadcast event and <html lang>
/// update as the order flow, so one choice follows the customer everywhere. */
export function useSiteLocale() {
  const flow = useFlowLocale();
  return { ...flow, t: getSiteCopy(flow.locale) };
}
